// 공용 확인 다이얼로그 (파괴적 동작용).
// 네이티브 window.confirm 을 대체한다. Promise 기반이라 호출부는 `if (await confirm(...))` 로 쓴다.
// 접근성: role=alertdialog + aria-modal, 포커스 트랩(Tab 순환), Esc 취소, 열릴 때 취소 버튼에 포커스
// (파괴적 동작은 안전한 기본 선택을 취소에 둔다), 닫힐 때 직전 포커스 복원.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { cn } from "./cn.ts";

export interface ConfirmOptions {
  // 다이얼로그 제목(짧게).
  title: string;
  // 본문 설명(무엇이 일어나는지, 되돌릴 수 있는지).
  description?: ReactNode;
  // 확인 버튼 라벨. 동작명을 명시한다(예: "삭제", "탈퇴"). 기본 "확인".
  confirmLabel?: string;
  // 취소 버튼 라벨. 기본 "취소".
  cancelLabel?: string;
  // 확인 버튼 색조. 파괴적이면 danger.
  tone?: "danger" | "brand";
}

// 호출부에 노출되는 confirm 함수 타입.
type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

// 확인 다이얼로그 컨텍스트 제공자. 앱 루트 근처에 한 번 감싸 둔다.
export function ConfirmProvider({ children }: { children: ReactNode }) {
  // 현재 열린 요청(옵션 + Promise resolve). null 이면 닫힘.
  const [pending, setPending] = useState<{
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending({ options, resolve });
    });
  }, []);

  // 확인/취소 처리: Promise 를 풀고 다이얼로그를 닫는다.
  const settle = useCallback(
    (value: boolean) => {
      setPending((cur) => {
        cur?.resolve(value);
        return null;
      });
    },
    [],
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending ? (
        <ConfirmDialog
          options={pending.options}
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        />
      ) : null}
    </ConfirmContext.Provider>
  );
}

// 확인 다이얼로그를 호출하는 훅. ConfirmProvider 하위에서만 사용.
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm 은 ConfirmProvider 안에서만 사용할 수 있습니다.");
  }
  return ctx;
}

// 실제 다이얼로그 표면. 포커스 트랩/Esc/포커스 복원을 담당.
function ConfirmDialog({
  options,
  onConfirm,
  onCancel,
}: {
  options: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const tone = options.tone ?? "danger";

  useEffect(() => {
    // 열릴 때 직전 포커스를 저장하고 취소 버튼으로 포커스를 옮긴다.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    cancelRef.current?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key !== "Tab") {
        return;
      }
      // 포커스 트랩: 패널 내부 포커스 가능한 요소들 사이만 순환.
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) {
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) {
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // 닫힐 때 원래 위치로 포커스 복원(키보드 사용자 맥락 유지).
      previouslyFocused?.focus?.();
    };
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
      // 오버레이 클릭은 취소로 처리(파괴적 동작을 실수로 확정하지 않게).
      onClick={onCancel}
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={options.description ? "confirm-dialog-desc" : undefined}
        className="w-full max-w-sm rounded-lg bg-surface p-5 shadow-elevated"
        // 패널 내부 클릭이 오버레이로 전파돼 닫히지 않도록 차단.
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-dialog-title" className="text-lg font-bold text-ink-900">
          {options.title}
        </h2>
        {options.description ? (
          <p id="confirm-dialog-desc" className="mt-2 text-sm text-ink-700">
            {options.description}
          </p>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="inline-flex min-h-touch select-none items-center justify-center rounded border border-ink-300 bg-surface px-4 text-base font-semibold text-ink-900 transition-colors duration-fast hover:bg-ink-50"
          >
            {options.cancelLabel ?? "취소"}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={cn(
              "inline-flex min-h-touch select-none items-center justify-center rounded px-4 text-base font-semibold text-white transition-colors duration-fast",
              tone === "danger"
                ? "bg-danger-600 hover:bg-danger-700"
                : "bg-brand-600 hover:bg-brand-700",
            )}
          >
            {options.confirmLabel ?? "확인"}
          </button>
        </div>
      </div>
    </div>
  );
}
